import React from "react"
import PropTypes from "prop-types"
import { Button, Col, FormLabel, FormControl, FormGroup, Row } from "react-bootstrap"
import { useClipboard } from "@mantine/hooks"

// components
import ExplainedToggle from "app/static/frontend/components/ExplainedToggle"
import Tooltip from "app/static/frontend/components/Tooltip"

import { publicSheetsConfig } from "app/static/frontend/sheets/components/helpTexts"
import "components/stylus/PublicFormComponent.styl"

/** A component to render a section of our form, asking whether
 * the user should make this item/section/feature public or not
 * Pass in all of your required information, such as the text you want to display
 * Pass in an action creator as onChange so that you can do things / update state
 * on users' toggle
 *
 * You can also pass in an additional component to renderAdditionalComponents
 */
const PublicFormComponent = (props) => {
    const linkClipboard = useClipboard({ timeout: 1500 })
    const iFrameClipboard = useClipboard({ timeout: 1500 })

    const {
        // Required Props
        publicUrl,
        publicIframeUrl,
        is_public,
        onChange,

        // Optional Props
        dataCy,
        onText,
        offText,
        onExplanation,
        offExplanation,
        size,
        showEmbed,
        renderAdditionalComponents,
    } = props

    return (
        <div className="public-form-component">
            {/* the button that activates / deactivates public functionality */}
            <ExplainedToggle
                {...(dataCy ? { "data-cy": dataCy } : {})}
                on={onText}
                onExplanation={onExplanation}
                offExplanation={offExplanation}
                off={offText}
                active={is_public} // eslint-disable-line camelcase
                size={size}
                onChange={onChange}
            />

            {renderAdditionalComponents && is_public ? renderAdditionalComponents : null}

            {/* the link and associated buttons with copy functionality */}
            {is_public && ( // eslint-disable-line camelcase
                <Row className="pad-top">
                    <Col sm={showEmbed ? 7 : 8}>
                        <FormControl
                            className="input-lg"
                            id="public-link"
                            value={publicUrl}
                            placeholder="Your sheet's link for sharing"
                            size="lg"
                            disabled
                        />
                    </Col>
                    <Col sm={showEmbed ? 2 : 4}>
                        <Tooltip label="Copy this sheet's sharing link to your clipboard">
                            <Button
                                variant="primary"
                                size="lg"
                                className="wrap-button btn-block"
                                onClick={() => linkClipboard.copy(publicUrl)}
                            >
                                {linkClipboard.copied ? `Copied!` : `Copy Link`}
                            </Button>
                        </Tooltip>
                    </Col>
                    {showEmbed && (
                        <Col sm={3}>
                            <Tooltip label="Copy an embed link of this sheet to your clipboard">
                                <Button
                                    variant="primary"
                                    size="lg"
                                    className="wrap-button btn-block"
                                    onClick={() => iFrameClipboard.copy(publicIframeUrl)}
                                >
                                    {iFrameClipboard.copied ? `Copied!` : `Copy Embed Link`}
                                </Button>
                            </Tooltip>
                        </Col>
                    )}
                </Row>
            )}
        </div>
    )
}

PublicFormComponent.propTypes = {
    onChange: PropTypes.func.isRequired, // the action creator that does something upon toggle
    publicUrl: PropTypes.string.isRequired, // the public url
    publicIframeUrl: PropTypes.string.isRequired, // the public iframe url
    is_public: PropTypes.bool.isRequired, // decides whether to display the link and buttons
    onExplanation: PropTypes.string,
    offExplanation: PropTypes.string,
    onText: PropTypes.string,
    offText: PropTypes.string,
    renderAdditionalComponents: PropTypes.oneOfType([PropTypes.bool, PropTypes.instanceOf(React.Component)]),
    size: PropTypes.string,
    showEmbed: PropTypes.bool,
}

PublicFormComponent.defaultProps = {
    onExplanation: "Anyone with the link can access this resouce",
    offExplanation: "Only your team can access this resource",
    onText: "On",
    offText: "Off",
    renderAdditionalComponents: false,
    size: "lg",
    showEmbed: true,
}

// Handles the password getting/displaying for external resources
export const ExternalFormPasswordComponent = (props) => (
    <FormGroup className="form-group">
        <FormLabel className="control-label">
            {"Require Password "}
            <Tooltip label={publicSheetsConfig.requirePassword.tooltipText(props.resourceName)} />
        </FormLabel>
        <ExplainedToggle
            active={props.passwordRequired} // eslint-disable-line camelcase
            off={publicSheetsConfig.requirePassword.offText}
            offExplanation={publicSheetsConfig.requirePassword.offExplanation(props.resourceName)}
            on={publicSheetsConfig.requirePassword.onText}
            onChange={props.updatePasswordRequired}
            onExplanation={publicSheetsConfig.requirePassword.onExplanation(props.resourceName)}
        />
        {props.passwordRequired && (
            <FormGroup>
                <br />
                <FormLabel>Password</FormLabel>
                <FormControl
                    id="public-sheets-password-input"
                    value={props.externalPassword}
                    placeholder={`Choose a password to limit access to your ${props.resourceName}...`}
                    bsSize="lg"
                    onChange={props.changePassword}
                />
            </FormGroup>
        )}
    </FormGroup>
)

ExternalFormPasswordComponent.propTypes = {
    changePassword: PropTypes.func.isRequired,
    updatePasswordRequired: PropTypes.func.isRequired,
    passwordRequired: PropTypes.bool.isRequired,
    externalPassword: PropTypes.string,
}

export default PublicFormComponent
